import { Fragment, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { Job } from '../../api/types'
import { JOB_STATUS, JobStatusBadge } from '../../components/StatusBadges'
import { Badge, Button, Card, CodeBlock, EmptyState, ErrorNote, Field, LoadingBlock } from '../../components/ui'
import { formatMs } from '../../components/admin/UsageTables'
import { useCancelJob, useEnqueueJob, useJobTypes, useJobs, useRetryFailed, useRetryJob, useWorkers } from '../../hooks/useAdmin'
import { elapsed, prettyJson, shortId, timeAgo } from '../../utils/format'

export function AdminJobsPage() {
  return (
    <div className="stack-lg">
      <EnqueueCard />
      <JobsCard />
      <WorkersCard />
    </div>
  )
}

function EnqueueCard() {
  const types = useJobTypes()
  const enqueue = useEnqueueJob()
  const [jobType, setJobType] = useState('')
  const [payload, setPayload] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)

  useEffect(() => {
    const first = types.data?.[0]
    if (first && !jobType) {
      setJobType(first.job_type)
      setPayload(prettyJson(first.example_payload))
    }
  }, [types.data, jobType])

  const selected = types.data?.find((t) => t.job_type === jobType)
  return (
    <Card title="Run a job">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          let parsed: unknown
          try {
            parsed = payload.trim() ? JSON.parse(payload) : {}
          } catch {
            setParseError('The payload is not valid JSON.')
            return
          }
          setParseError(null)
          enqueue.mutate({ job_type: jobType, payload: parsed })
        }}
      >
        <div className="grid-2">
          <div className="stack">
            <Field label="Job type">
              <select
                value={jobType}
                onChange={(e) => {
                  setJobType(e.target.value)
                  const next = types.data?.find((t) => t.job_type === e.target.value)
                  if (next) setPayload(prettyJson(next.example_payload))
                }}
              >
                {types.data?.map((t) => (
                  <option key={t.job_type} value={t.job_type}>{t.job_type}</option>
                ))}
              </select>
            </Field>
            {selected && (
              <p className="muted small">
                {selected.description} <Badge>lane: {selected.lane}</Badge>
              </p>
            )}
          </div>
          <Field label="Payload (JSON)">
            <textarea className="mono" rows={5} value={payload} onChange={(e) => setPayload(e.target.value)} spellCheck={false} />
          </Field>
        </div>
        {parseError && <ErrorNote message={parseError} />}
        {enqueue.error && <ErrorNote error={enqueue.error} />}
        <div className="row">
          <Button type="submit" busy={enqueue.isPending} disabled={!jobType}>Enqueue</Button>
          {enqueue.data && <span className="muted small">Queued job <code>{shortId(enqueue.data.id)}</code>.</span>}
        </div>
      </form>
    </Card>
  )
}

/** Recent jobs, filtered by status, type and (from `?workspace=`) workspace. */
function JobsCard() {
  const [params, setParams] = useSearchParams()
  const workspaceId = params.get('workspace') ?? ''
  const [status, setStatus] = useState('')
  const [jobType, setJobType] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const types = useJobTypes()
  const jobs = useJobs({ status, job_type: jobType, workspace_id: workspaceId })
  const retry = useRetryJob()
  const cancel = useCancelJob()
  const retryFailed = useRetryFailed()
  const clearWorkspace = () => {
    const next = new URLSearchParams(params)
    next.delete('workspace')
    setParams(next, { replace: true })
  }
  return (
    <Card
      title="Recent jobs"
      actions={
        <>
          {workspaceId && (
            <span className="chip">
              <Link to={`/studio/admin/workspaces/${workspaceId}`}>One workspace</Link>
              <button type="button" className="tx-link" onClick={clearWorkspace} aria-label="Show every workspace">×</button>
            </span>
          )}
          <select aria-label="Filter by type" value={jobType} onChange={(e) => setJobType(e.target.value)}>
            <option value="">All types</option>
            {types.data?.map((t) => (
              <option key={t.job_type} value={t.job_type}>{t.job_type}</option>
            ))}
          </select>
          <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(JOB_STATUS).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <Button
            variant="secondary"
            busy={retryFailed.isPending}
            onClick={() => retryFailed.mutate({ job_type: jobType || undefined, workspace_id: workspaceId || undefined })}
            title="Retry the latest failed jobs matching the type and workspace filters (up to 200)"
          >
            Retry failed
          </Button>
        </>
      }
    >
      {jobs.isPending && <LoadingBlock />}
      {jobs.isError && <ErrorNote error={jobs.error} />}
      {(retry.error ?? cancel.error ?? retryFailed.error) && <ErrorNote error={retry.error ?? cancel.error ?? retryFailed.error} />}
      {retryFailed.data && <p className="muted small">Put {retryFailed.data.retried} failed {retryFailed.data.retried === 1 ? 'job' : 'jobs'} back in the queue.</p>}
      {jobs.data?.length === 0 && <EmptyState title="No jobs yet" body="Enqueue one above." />}
      {jobs.data && jobs.data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Status</th><th>Type</th><th>Job</th><th>Attempts</th><th>Worker</th><th>Created</th><th>Took</th><th /></tr>
            </thead>
            <tbody>
              {jobs.data.map((job) => (
                <Fragment key={job.id}>
                  <tr className="clickable" onClick={() => setOpen(open === job.id ? null : job.id)}>
                    <td><JobStatusBadge status={job.status} /></td>
                    <td><code>{job.job_type}</code></td>
                    <td><code className="muted">{shortId(job.id)}</code></td>
                    <td>{job.attempts}/{job.max_attempts}</td>
                    <td className="muted truncate">{job.worker_id ?? '—'}</td>
                    <td className="muted">{timeAgo(job.created_at)}</td>
                    <td className="muted">{job.started_at ? elapsed(job.started_at, job.completed_at) : '—'}</td>
                    <td className="cell-actions">
                      {(job.status === 'ready' || job.status === 'leased') && (
                        <Button
                          variant="ghost"
                          busy={cancel.isPending && cancel.variables === job.id}
                          onClick={(event) => {
                            event.stopPropagation()
                            cancel.mutate(job.id)
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                      {job.status === 'failed' && (
                        <Button
                          variant="ghost"
                          busy={retry.isPending && retry.variables === job.id}
                          onClick={(event) => {
                            event.stopPropagation()
                            retry.mutate(job.id)
                          }}
                        >
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                  {open === job.id && (
                    <tr className="details">
                      <td colSpan={8}><JobDetails job={job} /></td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function JobDetails({ job }: { job: Job }) {
  return (
    <div className="grid-2">
      <div className="stack-sm">
        <div className="field-label">Payload</div>
        <CodeBlock value={job.payload} />
      </div>
      <div className="stack-sm">
        {job.error && (
          <>
            <div className="field-label">Error</div>
            <ErrorNote message={job.error} />
          </>
        )}
        {job.result != null && (
          <>
            <div className="field-label">Result</div>
            <CodeBlock value={job.result} />
          </>
        )}
        {job.progress != null && (
          <>
            <div className="field-label">Progress</div>
            <CodeBlock value={job.progress} />
          </>
        )}
        {!job.error && job.result == null && <p className="muted small">No result yet.</p>}
      </div>
    </div>
  )
}

function WorkersCard() {
  const workers = useWorkers()
  return (
    <Card title="Workers">
      {workers.isPending && <LoadingBlock />}
      {workers.isError && <ErrorNote error={workers.error} />}
      {workers.data?.length === 0 && <EmptyState title="No workers have registered" body="Start one with cargo run -p ifc-worker." />}
      {workers.data && workers.data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Worker</th><th>Kinds</th><th>Status</th><th>Last seen</th><th>Last 24 h</th><th>Avg time</th><th>All time</th><th>Version</th></tr></thead>
            <tbody>
              {workers.data.map((worker) => (
                <tr key={worker.id}>
                  <td><code>{worker.id}</code></td>
                  <td>{worker.kinds.join(', ')}</td>
                  <td>{worker.online ? <Badge tone="ok">Online</Badge> : <Badge>Offline</Badge>}</td>
                  <td className="muted">{timeAgo(worker.last_seen_at)}</td>
                  <td>{worker.jobs_24h} done{worker.failed_24h > 0 && <span className="error-text"> · {worker.failed_24h} failed</span>}</td>
                  <td className="muted">{worker.avg_ms_24h == null ? '—' : formatMs(worker.avg_ms_24h)}</td>
                  <td className="muted">{worker.jobs_succeeded} done · {worker.jobs_failed} failed</td>
                  <td className="muted">{worker.version ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
